import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'ravepal-install-prompt-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const wasDismissed = localStorage.getItem(DISMISS_KEY);
    if (wasDismissed) {
      const when = parseInt(wasDismissed, 10);
      if (Date.now() - when < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
      localStorage.removeItem(DISMISS_KEY);
    }

    if (isIOS()) {
      setShowIOS(true);
      return;
    }

    if (isAndroid()) {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowAndroid(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    setShowIOS(false);
    setShowAndroid(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowAndroid(false);
      setDismissed(true);
      setDeferredPrompt(null);
    }
  };

  if (dismissed || (!showIOS && !showAndroid)) return null;

  if (showIOS) {
    return (
      <div className="fixed bottom-20 left-2 right-2 z-50 flex items-center gap-3 rounded-lg bg-surface border border-primary/30 p-3 shadow-lg sm:left-4 sm:right-auto sm:max-w-sm">
        <img src="/pwa-64x64.png" alt="" className="h-10 w-10 flex-shrink-0 rounded-lg" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text">
            {t('install.installApp', { appName: t('common.appName') })}
          </p>
          <p className="text-xs text-text-secondary">
            {t('install.iosInstruction')}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 rounded p-1.5 text-text-secondary hover:bg-background hover:text-text transition-colors"
          aria-label={t('common.dismiss')}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  }

  if (showAndroid && deferredPrompt) {
    return (
      <div className="fixed bottom-20 left-2 right-2 z-50 flex items-center gap-3 rounded-lg bg-surface border border-primary/30 p-3 shadow-lg sm:left-4 sm:right-auto sm:max-w-sm">
        <img src="/pwa-64x64.png" alt="" className="h-10 w-10 flex-shrink-0 rounded-lg" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text">
            {t('install.installApp', { appName: t('common.appName') })}
          </p>
          <p className="text-xs text-text-secondary">{t('install.androidInstruction')}</p>
        </div>
        <div className="flex flex-shrink-0 gap-1">
          <button
            type="button"
            onClick={handleInstall}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-background hover:bg-primary/90 transition-colors"
          >
            {t('install.install')}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded p-1.5 text-text-secondary hover:bg-background hover:text-text transition-colors"
            aria-label={t('common.dismiss')}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return null;
}
